﻿import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Heading, Text, Button, Input, LoadingSpinner, SortableTableHeader } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { usePermission } from '../../hooks/usePermissions'
import { qualityService, findTicketStageIdByOrder, IncidentFilters } from '../../services/quality'
import { modulesService } from '../../services/modules'
import {
  QualityCategory,
  QualityParameter,
  QualityTicket as QualityIncident,
  QualityTicketAttachment,
  QualityTicketHistory as QualityIncidentHistory,
  QualityTicketOrigin as QualityIncidentOrigin,
  QualityTicketStage as QualityIncidentStage,
  QualityTicketType as QualityIncidentType,
  Severity,
  TicketKind,
  formatTicketNumber,
} from '../../types/quality'
import { buildDepartmentLabelMap, DEPARTMENT_RESP_LABEL, digitsOnlyInput, formatBrokerTicketLabel, formatProfilePickerLabel, formatRequesterDisplay, type QualityActiveProfile } from './qualidadeDisplay'
import { canUseNotApplicableKind, TICKET_KIND_COLORS, TICKET_KIND_LABELS } from './qualidadeKind'
import { buildDefaultDashFilters, hasActiveTicketFilters } from './qualidadeFilters'
import {
  addBusinessHours,
  computeMaxDeadline,
  formatBusinessHours,
  formatMaxDeadlineDatePtBr,
  getElapsedBusinessHoursMs,
  isMaxDeadlineBreached,
  MAX_DEADLINE_UNIT_LABELS,
  parseQualityBusinessDayWindow,
  parseSlaPrimeiraRespostaGroupIds,
  parseSlaHours,
} from './qualityBusinessHours'
import { TicketDetailDrawer as IncidentDetailDrawer } from './TicketDetailDrawer'
import { TicketEditModal as IncidentEditModal, isTicketFormValid } from './TicketEditModal'
import { PolicyNumberCombobox } from './PolicyNumberCombobox'
import { CheckboxMultiSelect } from './CheckboxMultiSelect'
import { ArrowDownTrayIcon, ArrowPathIcon, FunnelIcon } from '@heroicons/react/24/outline'
import { downloadXlsx } from '../../utils/downloadXlsx'
import { useAuthContext } from '../../contexts/AuthContext'
import { dateOnlyToTimestamptz, formatDateOnlyPtBr, localTodayYmd, parseDateOnlyYmd } from '../../utils/dateOnly'

const MODULE_NAME = 'qualidade_tickets'
const PAGE_SIZE = 100

const SEVERITY_LABELS: Record<Severity, string> = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
}

const SEVERITY_COLORS: Record<Severity, string> = {
  low: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  high: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
}

function buildNewTicketSuccessTitle(_kind: TicketKind, ticketNumber: number | null | undefined): string {
  if (ticketNumber != null) {
    return `N° ${ticketNumber}`
  }
  return '—'
}

/** Mensagem pós-salvamento apenas para novo ticket (Novo Registro). */
const NEW_TICKET_SUCCESS_COPY: Record<TicketKind, { body: string[] }> = {
  incident: {
    body: [
      'Recebemos seu registro e nossa equipe já irá analisar. Em breve, você terá um retorno.',
      'Obrigada por nos avisar! Sua contribuição é essencial para seguirmos melhorando continuamente!',
    ],
  },
  suggestion: {
    body: [
      'Que bom ter sua sugestão por aqui! Estamos avaliando e em breve você terá um retorno.',
      'Obrigada por fazer parte dessa construção com a gente! Sua contribuição faz toda a diferença.',
    ],
  },
  improvement: {
    body: [
      'Recebemos sua solicitação e nossa equipe já está analisando. Em breve, você terá um retorno.',
      'Obrigada por nos ajudar a evoluir. Melhorar faz parte da nossa jornada, e você é parte essencial dela.',
    ],
  },
  not_applicable: {
    body: [
      'O registo foi salvo e classificado como Não aplicável.',
      'Obrigada pelo registro!',
    ],
  },
}

/** Barra de título do modal de sucesso (novo ticket): fundo cinza, texto escuro/claro no modo dark. */
const SUCCESS_TITLE_BAR_CLASS =
  'bg-gray-200 text-gray-900 dark:bg-gray-600 dark:text-gray-100 border-b border-gray-300 dark:border-gray-500'

function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '')
  const r = parseInt(h.substring(0, 2), 16)
  const g = parseInt(h.substring(2, 4), 16)
  const b = parseInt(h.substring(4, 6), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

const KIND_FILTER_OPTIONS: { value: TicketKind; label: string }[] = [
  { value: 'incident', label: 'Incidente' },
  { value: 'improvement', label: 'Melhoria' },
  { value: 'suggestion', label: 'Sugestão' },
  { value: 'not_applicable', label: 'Não aplicável' },
]

const SEVERITY_FILTER_OPTIONS: { value: Severity; label: string }[] = [
  { value: 'low', label: 'Baixa' },
  { value: 'medium', label: 'Média' },
  { value: 'high', label: 'Alta' },
]

const DELETED_FILTER_OPTIONS: { value: 'active' | 'deleted'; label: string }[] = [
  { value: 'active', label: 'Não excluídos' },
  { value: 'deleted', label: 'Excluídos' },
]

type TicketSortKey =
  | 'ticket_number'
  | 'title'
  | 'kind'
  | 'occurred_at'
  | 'requester'
  | 'severity'
  | 'stage'
  | 'open_business_hours'
  | 'max_deadline'

function maxDeadlineTimestamp(inc: QualityIncident): number | null {
  const unit = inc.max_deadline_unit
  const value = inc.max_deadline_value
  if (!unit || !value || value <= 0) return null
  return computeMaxDeadline(inc.created_at, unit, value).getTime()
}

function severitySortRank(s: Severity | null | undefined): number {
  if (s === 'low') return 0
  if (s === 'medium') return 1
  if (s === 'high') return 2
  return 3
}

function formatDateTimePtBr(value: string | null | undefined): string {
  if (!value) return ''
  try {
    return new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
  } catch {
    return ''
  }
}

function formatRegisteredByDisplay(
  inc: QualityIncident,
  activeProfiles: QualityActiveProfile[],
): string {
  const fromJoin = inc.registered_by?.name?.trim()
  if (fromJoin) return fromJoin
  const uid = inc.registered_by_user_id
  if (uid) {
    const p = activeProfiles.find(x => x.id === uid)
    if (p?.name?.trim()) return p.name.trim()
  }
  return ''
}

const TICKET_TITLE_LIST_MAX_CHARS = 55

function formatTicketTitleListPreview(title: string): string {
  const t = title.trim()
  if (t.length <= TICKET_TITLE_LIST_MAX_CHARS) return t
  return `${t.slice(0, TICKET_TITLE_LIST_MAX_CHARS)}…`
}

// ---- Tela principal ----
const QualidadeIncidentes: React.FC = () => {
  const [searchParams] = useSearchParams()
  const { profile, user } = useAuthContext()
  const { hasPermission: canUpdate } = usePermission(MODULE_NAME, 'update')
  const { hasPermission: canDownloadInfo } = usePermission(MODULE_NAME, 'download_info')
  const [moduleTitle, setModuleTitle] = useState<string>('Tickets de Qualidade')
  const [moduleDescription, setModuleDescription] = useState<string>('Registro e gestão de tickets de qualidade')

  useEffect(() => {
    modulesService.getByName(MODULE_NAME).then(result => {
      if (result.success && result.data) {
        if (result.data.display_name) setModuleTitle(result.data.display_name)
        if (result.data.description) setModuleDescription(result.data.description)
      }
    })
  }, [])

  const [loading, setLoading] = useState(true)
  const [incidents, setIncidents] = useState<QualityIncident[]>([])
  const [stages, setStages] = useState<QualityIncidentStage[]>([])
  const [categories, setCategories] = useState<QualityCategory[]>([])
  const [departments, setDepartments] = useState<{ id: string; name: string; display_name: string }[]>([])
  const [activeProfiles, setActiveProfiles] = useState<QualityActiveProfile[]>([])
  const [incidentTypes, setIncidentTypes] = useState<QualityIncidentType[]>([])
  const [incidentOrigins, setIncidentOrigins] = useState<QualityIncidentOrigin[]>([])
  const [typeOriginLinks, setTypeOriginLinks] = useState<Map<string, string[]>>(new Map())
  const [qualityParameters, setQualityParameters] = useState<QualityParameter[]>([])
  const [improcedentReasons, setImprocedentReasons] = useState<Awaited<ReturnType<typeof qualityService.listImprocedentReasonsActive>>>([])
  /** ticket_id → ISO do primeiro comentário ativo (para cor do SLA na coluna Tempo Abertura). */
  const [firstCommentAtByTicketId, setFirstCommentAtByTicketId] = useState<Record<string, string>>({})

  // Filtros de servidor (últimos 30 dias por padrão)
  const [filters, setFilters] = useState<IncidentFilters>(buildDefaultDashFilters)
  // Busca local
  const [query, setQuery] = useState('')
  const [convenioCodeFilterDraft, setConvenioCodeFilterDraft] = useState('')

  useEffect(() => {
    const q = searchParams.get('q')?.trim()
    if (q) setQuery(q)
  }, [])

  useEffect(() => {
    setConvenioCodeFilterDraft(filters.convenioCode || '')
  }, [filters.convenioCode])

  const [sortBy, setSortBy] = useState<TicketSortKey>('ticket_number')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  // Paginação
  const [currentPage, setCurrentPage] = useState(1)

  // Modal de edição
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editing, setEditing] = useState<Partial<QualityIncident> | null>(null)
  const [saving, setSaving] = useState(false)
  /** Após novo ticket salvo: exibe modal com texto conforme tipo (melhoria vs incidente). */
  const [newTicketSuccess, setNewTicketSuccess] = useState<{
    kind: TicketKind
    ticketNumber: number | null | undefined
  } | null>(null)

  // Drawer de detalhe
  const [selectedIncident, setSelectedIncident] = useState<QualityIncident | null>(null)
  const [history, setHistory] = useState<QualityIncidentHistory[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)

  const load = useCallback(async (f?: IncidentFilters) => {
    setLoading(true)
    const [inc, st, cats, deps, profs, types, origins, links, params, improcedent] = await Promise.all([
      qualityService.listIncidents(f ?? filters),
      qualityService.listStages(),
      qualityService.listCategoriesActive(),
      qualityService.listDepartmentsActive(),
      qualityService.listActiveProfilesForPicker(),
      qualityService.listIncidentTypesActive(),
      qualityService.listIncidentOriginsActive(),
      qualityService.listTicketTypeOriginLinks(),
      qualityService.listQualityParameters(),
      qualityService.listImprocedentReasons(),
    ])
    const firstComments = await qualityService.listFirstCommentAtByTicketIds(
      inc.map(t => t.id),
      parseSlaPrimeiraRespostaGroupIds(params),
    )
    setFirstCommentAtByTicketId(Object.fromEntries(firstComments))
    setIncidents(inc)
    setStages(st)
    setCategories(cats)
    setDepartments(deps)
    setActiveProfiles(profs)
    setIncidentTypes(types)
    setIncidentOrigins(origins)
    setTypeOriginLinks(links)
    setQualityParameters(params)
    setImprocedentReasons(improcedent)
    setLoading(false)
  }, [filters])

  useEffect(() => { load() }, [load])

  const userDepartmentId = (profile as { department_id?: string | null } | null)?.department_id ?? null
  const canUseNotApplicable = useMemo(
    () => canUseNotApplicableKind(userDepartmentId, qualityParameters),
    [userDepartmentId, qualityParameters],
  )

  const applyFilter = (patch: Partial<IncidentFilters>) => {
    const next = { ...filters, ...patch }
    setFilters(next)
    setCurrentPage(1)
    load(next)
  }

  const clearFilters = () => {
    const empty: IncidentFilters = {}
    setFilters(empty)
    setQuery('')
    setConvenioCodeFilterDraft('')
    setCurrentPage(1)
    load(empty)
  }

  const commitConvenioCodeFilter = () => {
    const next = digitsOnlyInput(convenioCodeFilterDraft) || undefined
    if (next === (filters.convenioCode || undefined)) return
    applyFilter({ convenioCode: next })
  }

  const handleRefresh = useCallback(async () => {
    await load(filters)
  }, [load, filters])

  const departmentLabelMap = useMemo(() => buildDepartmentLabelMap(departments), [departments])

  const businessDayWindow = useMemo(
    () => parseQualityBusinessDayWindow(qualityParameters),
    [qualityParameters],
  )

  /** Horas úteis decorridas (seg–sex, dentro da janela hora_util_*) desde created_at. */
  const incidentBusinessHoursMs = useCallback(
    (inc: QualityIncident): number => {
      const start = new Date(inc.created_at)
      const end   = inc.closed_at ? new Date(inc.closed_at) : new Date()
      return getElapsedBusinessHoursMs(
        start,
        end,
        businessDayWindow.dayStartHour,
        businessDayWindow.dayEndHour,
      )
    },
    [businessDayWindow],
  )

  /**
   * SLA de 1ª resposta: vermelho se vencido; verde se dentro do prazo.
   * `null` quando não há horas de SLA configuradas para o tipo de ticket.
   */
  const slaFirstResponseBreached = useCallback(
    (inc: QualityIncident): boolean | null => {
      const slaHours = parseSlaHours(qualityParameters, inc.kind)
      if (slaHours == null) return null
      const start = new Date(inc.created_at)
      const deadline = addBusinessHours(
        start,
        slaHours,
        businessDayWindow.dayStartHour,
        businessDayWindow.dayEndHour,
      )
      const firstIso = firstCommentAtByTicketId[inc.id]
      const firstDate = firstIso ? new Date(firstIso) : null
      if (firstDate) return firstDate.getTime() > deadline.getTime()
      return Date.now() > deadline.getTime()
    },
    [businessDayWindow, qualityParameters, firstCommentAtByTicketId],
  )

  /** Corretores únicos que aparecem nos tickets carregados (para o filtro). */
  const brokersInTickets = useMemo(() => {
    const seen = new Map<string, { id: string; name: string; id_scgp: number | null }>()
    incidents.forEach(inc => {
      if (inc.broker_id && inc.broker) seen.set(inc.broker_id, inc.broker)
    })
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
  }, [incidents])

  const stageFilterOptions = useMemo(
    () => stages.map(s => ({ value: s.id, label: s.name })),
    [stages],
  )
  const categoryFilterOptions = useMemo(
    () => categories.map(c => ({ value: c.id, label: c.display_name })),
    [categories],
  )
  const departmentFilterOptions = useMemo(
    () => departments.map(d => ({ value: d.id, label: d.display_name })),
    [departments],
  )
  const requesterFilterOptions = useMemo(
    () => activeProfiles.map(p => ({
      value: p.id,
      label: formatProfilePickerLabel(p, departmentLabelMap),
    })),
    [activeProfiles, departmentLabelMap],
  )
  const ticketTypeFilterOptions = useMemo(
    () => incidentTypes.map(t => ({ value: t.id, label: t.display_name })),
    [incidentTypes],
  )
  const originFilterOptions = useMemo(
    () => incidentOrigins.map(o => ({ value: o.id, label: o.display_name })),
    [incidentOrigins],
  )
  const brokerFilterOptions = useMemo(
    () => brokersInTickets.map(b => ({ value: b.id, label: formatBrokerTicketLabel(b) })),
    [brokersInTickets],
  )

  const hasFilters = !!query || hasActiveTicketFilters(filters)

  const filtered = useMemo(() => {
    if (!query) return incidents
    const q = query.toLowerCase()
    const qNum = q.replace(/^#/, '')
    return incidents.filter(i =>
      i.title.toLowerCase().includes(q) ||
      (i.description || '').toLowerCase().includes(q) ||
      (i.justification || '').toLowerCase().includes(q) ||
      (i.external_link || '').toLowerCase().includes(q) ||
      (qNum && /^\d+$/.test(qNum) && String(i.ticket_number) === qNum) ||
      (qNum && /^\d+$/.test(qNum) && i.policy_number != null && String(i.policy_number).includes(qNum)) ||
      (qNum && /^\d+$/.test(qNum) && i.convenio_code != null && String(i.convenio_code).includes(qNum))
    )
  }, [incidents, query])

  const toggleSort = (key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(key as TicketSortKey)
      setSortOrder('asc')
    }
  }

  const sortedFiltered = useMemo(() => {
    const arr = [...filtered]
    arr.sort((a, b) => {
      let res = 0
      switch (sortBy) {
        case 'ticket_number': {
          res = (a.ticket_number ?? 0) - (b.ticket_number ?? 0)
          break
        }
        case 'title': {
          res = (a.title || '').localeCompare(b.title || '', 'pt-BR', { sensitivity: 'base' })
          break
        }
        case 'kind': {
          res = a.kind.localeCompare(b.kind, 'pt-BR')
          break
        }
        case 'occurred_at': {
          const da = parseDateOnlyYmd(a.occurred_at) ?? ''
          const db = parseDateOnlyYmd(b.occurred_at) ?? ''
          res = da.localeCompare(db)
          break
        }
        case 'requester': {
          const sa = formatRequesterDisplay(a.requester, activeProfiles, departmentLabelMap, a.requester_user_id, { firstNameOnly: true })
          const sb = formatRequesterDisplay(b.requester, activeProfiles, departmentLabelMap, b.requester_user_id, { firstNameOnly: true })
          res = sa.localeCompare(sb, 'pt-BR', { sensitivity: 'base' })
          break
        }
        case 'severity': {
          res = severitySortRank(a.severity) - severitySortRank(b.severity)
          break
        }
        case 'stage': {
          const sa = a.stage?.name || ''
          const sb = b.stage?.name || ''
          res = sa.localeCompare(sb, 'pt-BR', { sensitivity: 'base' })
          break
        }
        case 'open_business_hours': {
          res = incidentBusinessHoursMs(a) - incidentBusinessHoursMs(b)
          break
        }
        case 'max_deadline': {
          const da = maxDeadlineTimestamp(a)
          const db = maxDeadlineTimestamp(b)
          if (da == null && db == null) res = 0
          else if (da == null) res = 1
          else if (db == null) res = -1
          else res = da - db
          break
        }
        default:
          break
      }
      if (res !== 0) return sortOrder === 'asc' ? res : -res
      return (b.ticket_number ?? 0) - (a.ticket_number ?? 0)
    })
    return arr
  }, [filtered, sortBy, sortOrder, activeProfiles, departmentLabelMap, incidentBusinessHoursMs])

  // Paginação client-side
  const totalPages = Math.ceil(sortedFiltered.length / PAGE_SIZE)
  const startIndex = (currentPage - 1) * PAGE_SIZE
  const endIndex = Math.min(currentPage * PAGE_SIZE, sortedFiltered.length)
  const paginated = sortedFiltered.slice(startIndex, endIndex)

  const exportTicketsExcel = () => {
    if (!canDownloadInfo || sortedFiltered.length === 0) return
    const rows = sortedFiltered.map((inc) => {
      const openMs = incidentBusinessHoursMs(inc)
      const showTempoDash = !Number.isFinite(openMs) || openMs <= 0
      const slaHours = parseSlaHours(qualityParameters, inc.kind)
      const slaDeadline = slaHours != null
        ? addBusinessHours(
            new Date(inc.created_at),
            slaHours,
            businessDayWindow.dayStartHour,
            businessDayWindow.dayEndHour,
          )
        : null
      const firstResponseIso = firstCommentAtByTicketId[inc.id]
      const slaBreached = slaFirstResponseBreached(inc)
      return {
        ID: formatTicketNumber(inc.ticket_number),
        Título: inc.title,
        'Tipo de Registro': TICKET_KIND_LABELS[inc.kind],
        Descrição: inc.description || '',
        Justificativa: inc.justification || '',
        'Data de ocorrência': formatDateOnlyPtBr(inc.occurred_at),
        Solicitante: formatRequesterDisplay(inc.requester, activeProfiles, departmentLabelMap, inc.requester_user_id),
        Categoria: inc.category?.display_name || '',
        [DEPARTMENT_RESP_LABEL]: inc.department?.display_name || '',
        Prioridade: inc.severity ? SEVERITY_LABELS[inc.severity] : '',
        Etapa: inc.stage?.name || '',
        Onde: inc.origin?.display_name || '',
        Motivo: inc.ticket_type?.display_name || '',
        'Registo improcedente': inc.improcedent_reason?.display_name || '',
        Corretor: inc.broker ? formatBrokerTicketLabel(inc.broker) : '',
        Apólice: inc.policy_number != null ? String(inc.policy_number) : '',
        Subgrupo: inc.convenio_code != null ? String(inc.convenio_code) : '',
        'Link externo': inc.external_link || '',
        Resolução: inc.resolution || '',
        'Encerrado em': inc.closed_at ? formatDateTimePtBr(inc.closed_at) : '',
        'Cadastrado por': formatRegisteredByDisplay(inc, activeProfiles),
        'Criado em': formatDateTimePtBr(inc.created_at),
        'Atualizado em': formatDateTimePtBr(inc.updated_at),
        'Tempo Abertura (Horas Úteis)': showTempoDash ? '' : formatBusinessHours(openMs),
        'Prazo máximo (unidade)': inc.max_deadline_unit
          ? (MAX_DEADLINE_UNIT_LABELS[inc.max_deadline_unit] ?? inc.max_deadline_unit)
          : '',
        'Prazo máximo (valor)': inc.max_deadline_value != null && inc.max_deadline_value > 0
          ? String(inc.max_deadline_value)
          : '',
        'Prazo máximo (data limite)': (() => {
          const unit = inc.max_deadline_unit
          const value = inc.max_deadline_value
          if (!unit || !value || value <= 0) return ''
          const deadline = computeMaxDeadline(inc.created_at, unit, value)
          return formatMaxDeadlineDatePtBr(deadline)
        })(),
        'Obs. do prazo': inc.max_deadline_notes || '',
        'Prazo máximo vencido': (() => {
          const unit = inc.max_deadline_unit
          const value = inc.max_deadline_value
          if (!unit || !value || value <= 0) return ''
          const deadline = computeMaxDeadline(inc.created_at, unit, value)
          return isMaxDeadlineBreached(deadline, inc.closed_at) ? 'Sim' : 'Não'
        })(),
        'Prazo máximo SLA': slaDeadline ? formatDateTimePtBr(slaDeadline.toISOString()) : '',
        '1ª Resposta em': firstResponseIso ? formatDateTimePtBr(firstResponseIso) : '',
        'SLA vencido': slaBreached == null ? '' : slaBreached ? 'Sim' : 'Não',
        'Excluído em': inc.deleted_at ? formatDateTimePtBr(inc.deleted_at) : '',
        'Excluído por': inc.deleter?.name || '',
      }
    })
    const dateStamp = new Date().toISOString().slice(0, 10)
    downloadXlsx('Tickets', `tickets-qualidade-${dateStamp}.xlsx`, rows)
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(p => p - 1)
  }
  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(p => p + 1)
  }
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page)
  }

  // Resetar página ao filtrar localmente ou ao mudar ordenação
  useEffect(() => { setCurrentPage(1) }, [query, sortBy, sortOrder])

  const openNew = () => {
    const initialStageId = findTicketStageIdByOrder(stages)
    const requesterId = profile?.id ?? user?.id ?? null
    setEditing({
      title: '',
      description: '',
      justification: '',
      kind: 'incident',
      stage_id: initialStageId ?? '',
      category_id: null,
      department_id: null,
      severity: null,
      occurred_at: dateOnlyToTimestamptz(localTodayYmd()),
      external_link: null,
      resolution: null,
      broker_id: null,
      policy_number: null,
      convenio_code: null,
      max_deadline_unit: null,
      max_deadline_value: null,
      max_deadline_notes: null,
      requester_user_id: requesterId,
    })
    setIsModalOpen(true)
  }

  const openEdit = (inc: QualityIncident) => {
    setEditing({ ...inc })
    setIsModalOpen(true)
    setSelectedIncident(null)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditing(null)
  }

  const save = async (pendingFiles: File[], toDelete: QualityTicketAttachment[]) => {
    if (
      !editing
      || !isTicketFormValid(editing, {
        severityRequired: canUseNotApplicable,
        maxDeadlineEditable: canUseNotApplicable,
      })
    ) return
    const wasNew = !editing.id
    const kindForSuccess = editing.kind
    setSaving(true)
    try {
      const saved = await qualityService.upsertIncident(editing)
      const ticketId = saved?.id ?? editing.id
      if (ticketId) {
        await Promise.all(toDelete.map(att => qualityService.deleteTicketAttachment(att)))
        await Promise.all(pendingFiles.map(file => qualityService.uploadTicketAttachment(ticketId, file)))
      }
      closeModal()
      if (wasNew && kindForSuccess) {
        setNewTicketSuccess({
          kind: kindForSuccess,
          ticketNumber: saved?.ticket_number,
        })
      }
      await load()
    } catch (err: any) {
      const msg = err?.message ?? err?.error_description ?? JSON.stringify(err)
      alert(`Erro ao salvar ticket: ${msg}`)
    } finally {
      setSaving(false)
    }
  }

  const remove = async (id: string) => {
    if (!window.confirm(
      'Excluir este ticket? A exclusão é lógica: some da lista e dos indicadores, ' +
      'mas o registro permanece com usuário e data da exclusão.'
    )) return
    try {
      await qualityService.deleteIncident(id)
      if (selectedIncident?.id === id) closeDrawer()
      await load()
    } catch {
      alert('Não foi possível excluir este ticket.')
    }
  }

  const openDrawer = async (inc: QualityIncident) => {
    setSelectedIncident(inc)
    setHistory([])
    setHistoryLoading(true)
    try {
      const [fresh, h] = await Promise.all([
        qualityService.getTicketById(inc.id),
        qualityService.listHistory(inc.id),
      ])
      if (fresh) setSelectedIncident(fresh)
      setHistory(h)
    } finally {
      setHistoryLoading(false)
    }
  }

  const closeDrawer = () => setSelectedIncident(null)

  return (
    <>
    <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard
          moduleName={MODULE_NAME}
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">Acesso Negado</Text>
                <Text size="sm" color="muted">Você não tem permissão para acessar esta página.</Text>
              </div>
            </div>
          }
        >
          <div className="py-6 px-4 sm:px-6 lg:px-8">

            {/* Cabeçalho */}
            <div className="mb-8">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <img
                    src="/selo-qualidade.png"
                    alt="Selo de qualidade"
                    className="h-14 w-auto flex-shrink-0 object-contain select-none"
                  />
                  <div className="min-w-0">
                    <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">{moduleTitle}</Heading>
                    <Text size="sm" color="muted" className="mt-2">{moduleDescription}</Text>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {canDownloadInfo && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={exportTicketsExcel}
                      disabled={loading || sortedFiltered.length === 0}
                      title={
                        sortedFiltered.length === 0
                          ? 'Nenhum registro para exportar'
                          : 'Baixar Excel com a relação filtrada'
                      }
                    >
                      <ArrowDownTrayIcon className="h-4 w-4 mr-1.5 inline" aria-hidden />
                      Baixar Excel
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={loading}
                    className="inline-flex items-center gap-2"
                    aria-label="Atualizar lista de tickets"
                  >
                    <ArrowPathIcon
                      className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}
                      aria-hidden
                    />
                    Atualizar
                  </Button>
                  <PermissionGuard moduleName={MODULE_NAME} permissionType="insert" fallback={null}>
                    <Button variant="solid" onClick={openNew}>Novo Registro</Button>
                  </PermissionGuard>
                </div>
              </div>
            </div>

            {/* Painel de filtros */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6 overflow-visible">
              <div className="flex items-center mb-4">
                <FunnelIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2" />
                <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">Filtros</Heading>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3 overflow-visible [&>div]:relative [&>div]:overflow-visible">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Tipo de Registro</label>
                  <CheckboxMultiSelect
                    options={KIND_FILTER_OPTIONS}
                    selected={filters.kinds ?? []}
                    onChange={kinds => applyFilter({ kinds: kinds.length ? kinds as TicketKind[] : undefined })}
                    placeholder="Todos"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Etapa</label>
                  <CheckboxMultiSelect
                    options={stageFilterOptions}
                    selected={filters.stageIds ?? []}
                    onChange={stageIds => applyFilter({ stageIds: stageIds.length ? stageIds : undefined })}
                    placeholder="Todas"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Prioridade</label>
                  <CheckboxMultiSelect
                    options={SEVERITY_FILTER_OPTIONS}
                    selected={filters.severities ?? []}
                    onChange={severities => applyFilter({ severities: severities.length ? severities as Severity[] : undefined })}
                    placeholder="Todas"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Categoria</label>
                  <CheckboxMultiSelect
                    options={categoryFilterOptions}
                    selected={filters.categoryIds ?? []}
                    onChange={categoryIds => applyFilter({ categoryIds: categoryIds.length ? categoryIds : undefined })}
                    placeholder="Todas"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{DEPARTMENT_RESP_LABEL}</label>
                  <CheckboxMultiSelect
                    options={departmentFilterOptions}
                    selected={filters.departmentIds ?? []}
                    onChange={departmentIds => applyFilter({ departmentIds: departmentIds.length ? departmentIds : undefined })}
                    placeholder="Todos"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Motivo</label>
                  <CheckboxMultiSelect
                    options={ticketTypeFilterOptions}
                    selected={filters.ticketTypeIds ?? []}
                    onChange={ticketTypeIds => applyFilter({ ticketTypeIds: ticketTypeIds.length ? ticketTypeIds : undefined })}
                    placeholder="Todos"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Onde</label>
                  <CheckboxMultiSelect
                    options={originFilterOptions}
                    selected={filters.originIds ?? []}
                    onChange={originIds => applyFilter({ originIds: originIds.length ? originIds : undefined })}
                    placeholder="Todas"
                  />
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-end gap-3 min-w-0 overflow-visible [&>div]:relative [&>div]:overflow-visible">
                  <div className="min-w-[12rem] w-full sm:w-56 lg:w-64 max-w-full">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Solicitante</label>
                    <CheckboxMultiSelect
                      options={requesterFilterOptions}
                      selected={filters.requesterUserIds ?? []}
                      onChange={requesterUserIds => applyFilter({ requesterUserIds: requesterUserIds.length ? requesterUserIds : undefined })}
                      placeholder="Todos"
                    />
                  </div>
                  <div className="min-w-[11rem] w-full sm:w-56 max-w-full">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Corretor</label>
                    <CheckboxMultiSelect
                      options={brokerFilterOptions}
                      selected={filters.brokerIds ?? []}
                      onChange={brokerIds => applyFilter({ brokerIds: brokerIds.length ? brokerIds : undefined })}
                      placeholder="Todos"
                      disabled={brokersInTickets.length === 0}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Apólice</label>
                    <PolicyNumberCombobox
                      value={filters.policyNumber ?? null}
                      onChange={val => applyFilter({ policyNumber: val ?? undefined })}
                      placeholder="Todas"
                      className="max-w-[11rem]"
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Subgrupo</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="Número"
                      value={convenioCodeFilterDraft}
                      onChange={e => setConvenioCodeFilterDraft(digitsOnlyInput(e.target.value))}
                      onBlur={commitConvenioCodeFilter}
                      onKeyDown={e => { if (e.key === 'Enter') commitConvenioCodeFilter() }}
                      className="w-full max-w-[11rem] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Ocorrência de</label>
                    <input
                      type="date"
                      value={filters.dateFrom || ''}
                      onChange={e => applyFilter({ dateFrom: e.target.value || undefined })}
                      className="w-full max-w-[11rem] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">até</label>
                    <input
                      type="date"
                      value={filters.dateTo || ''}
                      onChange={e => applyFilter({ dateTo: e.target.value || undefined })}
                      className="w-full max-w-[11rem] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div className="min-w-[11rem] w-full sm:w-auto max-w-full sm:max-w-[14rem]">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Exclusão</label>
                    <CheckboxMultiSelect
                      options={DELETED_FILTER_OPTIONS}
                      selected={filters.deletedFilters ?? []}
                      onChange={deletedFilters => applyFilter({
                        deletedFilters: deletedFilters.length
                          ? deletedFilters as Array<'active' | 'deleted'>
                          : undefined,
                        deletedFilter: undefined,
                      })}
                      placeholder="Não excluídos"
                    />
                  </div>
                  <div className="flex-1 min-w-[220px] max-w-full w-full sm:w-auto">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Busca por texto</label>
                    <Input
                      placeholder="Buscar por ID, título, descrição ou link externo..."
                      value={query}
                      onChange={(e: any) => setQuery(e.target.value)}
                    />
                  </div>
                  {hasFilters && (
                    <Button variant="ghost" onClick={clearFilters}>Limpar filtros</Button>
                  )}
              </div>
            </div>

            {/* Tabela */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900/30">
                    <tr>
                      <SortableTableHeader
                        sortKey="ticket_number"
                        currentSort={sortBy}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        compact
                        className="w-12 shrink-0 !pl-3 !pr-2"
                      >
                        ID
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="title"
                        currentSort={sortBy}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        compact
                        className="!pl-1"
                      >
                        Título
                      </SortableTableHeader>
                      <SortableTableHeader sortKey="kind" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} compact>
                        Tipo
                      </SortableTableHeader>
                      <SortableTableHeader sortKey="occurred_at" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} compact>
                        Ocorrência
                      </SortableTableHeader>
                      <SortableTableHeader sortKey="requester" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} compact>
                        Solicitante
                      </SortableTableHeader>
                      <SortableTableHeader sortKey="severity" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} compact>
                        Prioridade
                      </SortableTableHeader>
                      <SortableTableHeader sortKey="stage" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} compact>
                        Etapa
                      </SortableTableHeader>
                      <SortableTableHeader sortKey="open_business_hours" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} compact>
                        Tempo Abertura
                      </SortableTableHeader>
                      <SortableTableHeader sortKey="max_deadline" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} compact>
                        Prazo máximo
                      </SortableTableHeader>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {loading ? (
                      <tr>
                        <td colSpan={9} className="px-2 py-10 text-center">
                          <LoadingSpinner />
                        </td>
                      </tr>
                    ) : paginated.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="px-2 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                          Nenhum ticket encontrado.
                        </td>
                      </tr>
                    ) : (
                      paginated.map((inc) => {
                        const openMs = incidentBusinessHoursMs(inc)
                        const showTempoDash = !Number.isFinite(openMs) || openMs <= 0
                        const breached = slaFirstResponseBreached(inc)
                        const tempoAberturaTone = showTempoDash
                          ? 'text-gray-600 dark:text-gray-300'
                          : breached === true
                            ? 'text-red-600 dark:text-red-400 font-medium'
                            : breached === false
                              ? 'text-green-600 dark:text-green-400 font-medium'
                              : 'text-gray-600 dark:text-gray-300'
                        const maxDeadlineTs = maxDeadlineTimestamp(inc)
                        const maxDeadlineDate = maxDeadlineTs != null ? new Date(maxDeadlineTs) : null
                        const maxDeadlineBreached = maxDeadlineDate
                          ? isMaxDeadlineBreached(maxDeadlineDate, inc.closed_at)
                          : null
                        const maxDeadlineTone = maxDeadlineDate == null
                          ? 'text-gray-600 dark:text-gray-300'
                          : maxDeadlineBreached === true
                            ? 'text-red-600 dark:text-red-400 font-medium'
                            : maxDeadlineBreached === false
                              ? 'text-green-600 dark:text-green-400 font-medium'
                              : 'text-gray-600 dark:text-gray-300'
                        return (
                        <tr
                          key={inc.id}
                          onClick={() => openDrawer(inc)}
                          className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                        >
                          <td
                            className={`pl-3 pr-2 py-2 text-sm font-mono whitespace-nowrap ${
                              inc.deleted_at
                                ? 'text-red-600 dark:text-red-400 font-semibold'
                                : 'text-gray-600 dark:text-gray-300'
                            }`}
                          >
                            {formatTicketNumber(inc.ticket_number)}
                          </td>
                          <td className="pl-1 pr-2 py-2 text-sm font-normal text-gray-900 dark:text-white" title={inc.title}>
                            {formatTicketTitleListPreview(inc.title)}
                          </td>
                          <td className="px-2 py-2 text-sm whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${TICKET_KIND_COLORS[inc.kind]}`}>
                              {TICKET_KIND_LABELS[inc.kind]}
                            </span>
                          </td>
                          <td className="px-2 py-2 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {formatDateOnlyPtBr(inc.occurred_at)}
                          </td>
                          <td className="px-2 py-2 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                            {formatRequesterDisplay(inc.requester, activeProfiles, departmentLabelMap, inc.requester_user_id, { firstNameOnly: true })}
                          </td>
                          <td className="px-2 py-2 text-sm whitespace-nowrap">
                            {inc.severity ? (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${SEVERITY_COLORS[inc.severity]}`}>
                                {SEVERITY_LABELS[inc.severity]}
                              </span>
                            ) : '—'}
                          </td>
                          <td className="px-2 py-2 text-sm whitespace-nowrap">
                            {inc.stage ? (
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                style={inc.stage.color ? {
                                  backgroundColor: hexToRgba(inc.stage.color, 0.15),
                                  color: inc.stage.color,
                                  border: `1px solid ${hexToRgba(inc.stage.color, 0.4)}`,
                                } : undefined}
                              >
                                {inc.stage.name}
                              </span>
                            ) : '—'}
                          </td>
                          <td className={`px-2 py-2 text-sm whitespace-nowrap tabular-nums ${tempoAberturaTone}`}>
                            {showTempoDash ? '—' : formatBusinessHours(openMs)}
                          </td>
                          <td className={`px-2 py-2 text-sm whitespace-nowrap tabular-nums ${maxDeadlineTone}`}>
                            {maxDeadlineDate
                              ? formatMaxDeadlineDatePtBr(maxDeadlineDate)
                              : '—'}
                          </td>
                        </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Paginação */}
              {!loading && totalPages > 0 && (
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <Text size="sm" className="text-gray-700 dark:text-gray-300">
                      {sortedFiltered.length === 0
                        ? 'Nenhum registro'
                        : `Mostrando ${startIndex + 1} a ${endIndex} de ${sortedFiltered.length} registro${sortedFiltered.length !== 1 ? 's' : ''}`}
                    </Text>

                    {totalPages > 1 && (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={handlePreviousPage}
                          disabled={currentPage === 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Anterior
                        </button>

                        <div className="flex space-x-1">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNumber: number
                            if (totalPages <= 5) {
                              pageNumber = i + 1
                            } else if (currentPage <= 3) {
                              pageNumber = i + 1
                            } else if (currentPage >= totalPages - 2) {
                              pageNumber = totalPages - 4 + i
                            } else {
                              pageNumber = currentPage - 2 + i
                            }
                            return (
                              <button
                                key={pageNumber}
                                onClick={() => handlePageChange(pageNumber)}
                                className={`px-3 py-2 text-sm font-medium rounded-md ${
                                  currentPage === pageNumber
                                    ? 'bg-primary-600 text-white'
                                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                }`}
                              >
                                {pageNumber}
                              </button>
                            )
                          })}
                        </div>

                        <button
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Próximo
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

          </div>
        </PermissionGuard>
      </div>

      {/* Modal de criação/edição */}
      <IncidentEditModal
        open={isModalOpen && !!editing}
        editing={editing || {}}
        onEditingChange={setEditing}
        stages={stages}
        categories={categories}
        departments={departments}
        activeProfiles={activeProfiles}
        incidentTypes={incidentTypes}
        incidentOrigins={incidentOrigins}
        typeOriginLinks={typeOriginLinks}
        improcedentReasons={improcedentReasons.filter(r => r.is_active)}
        qualityParameters={qualityParameters}
        canUseNotApplicableKind={canUseNotApplicable}
        saving={saving}
        onClose={closeModal}
        onSave={save}
      />

      {newTicketSuccess && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ticket-success-title"
        >
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg overflow-hidden">
            <div
              id="ticket-success-title"
              className={`px-4 py-3 text-center ${SUCCESS_TITLE_BAR_CLASS}`}
            >
              <Text as="p" size="xl" weight="semibold" className="m-0">
                {buildNewTicketSuccessTitle(newTicketSuccess.kind, newTicketSuccess.ticketNumber)}
              </Text>
            </div>
            <div className="p-6">
            <div className="flex gap-4 items-center">
              <img
                src="/joinha.png"
                alt=""
                className="w-[100px] h-auto flex-shrink-0 object-contain select-none"
                width={100}
                height={100}
              />
              <div className="flex-1 min-w-0 space-y-3 text-center">
                {NEW_TICKET_SUCCESS_COPY[newTicketSuccess.kind].body.map((paragraph, i, arr) => {
                  const boldLast =
                    (newTicketSuccess.kind === 'incident' ||
                      newTicketSuccess.kind === 'suggestion' ||
                      newTicketSuccess.kind === 'improvement') &&
                    i === arr.length - 1
                  return (
                    <Text
                      key={i}
                      size="sm"
                      as="p"
                      className="m-0"
                      weight={boldLast ? 'bold' : 'normal'}
                    >
                      {paragraph}
                    </Text>
                  )
                })}
              </div>
            </div>
            <div className="mt-6 flex justify-center">
              <Button variant="solid" onClick={() => setNewTicketSuccess(null)}>
                OK
              </Button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Drawer de detalhe */}
      {selectedIncident && (
        <IncidentDetailDrawer
          incident={selectedIncident}
          stages={stages}
          categories={categories}
          departments={departments}
          activeProfiles={activeProfiles}
          incidentTypes={incidentTypes}
          incidentOrigins={incidentOrigins}
          history={history}
          historyLoading={historyLoading}
          onClose={closeDrawer}
          onEdit={openEdit}
          onDelete={remove}
          editPermissionModuleName={MODULE_NAME}
          qualityParameters={qualityParameters}
          improcedentReasons={improcedentReasons}
        />
      )}
    </>
  )
}

export default QualidadeIncidentes
